import { useCallback, useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import { useLocation } from 'react-router-dom';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { useLazyGetAddressesListQuery } from '@/store/services/address';
import { getUser } from '@/utils/localStorage';
import ChatBoxDialog from './ChatBoxDialog';
import type { ChatAddress } from './types';

const EXCLUDED_PATH_PREFIXES = ['/apps/settings', '/apps/billing', '/apps/profile'];

/** Compact variant of the chatbox “AI” blue glow — same palette, smaller spread */
const CHAT_PRO_LAUNCHER_SHADOW_IDLE =
	'0 0 0 1px rgba(147, 197, 253, 0.5), 0 2px 8px rgba(43, 127, 255, 0.2), 0 4px 14px rgba(56, 189, 248, 0.14), 0 0 20px rgba(59, 130, 246, 0.1)';
const CHAT_PRO_LAUNCHER_SHADOW_HOVER =
	'0 0 0 1px rgba(147, 197, 253, 0.62), 0 3px 10px rgba(43, 127, 255, 0.24), 0 6px 18px rgba(56, 189, 248, 0.16), 0 0 26px rgba(59, 130, 246, 0.12)';

/**
 * Chat address picker: include every enabled address only.
 * - Ignore `application` (linked application does not exclude an address).
 * - Modal opens when this list has length > 1; single address opens chat directly.
 * - Treat 1, true, and "1" as enabled for production API quirks.
 */
function filterAddressesForChatPicker(raw: ChatAddress[]) {
	return raw.filter((address) => Number(address.is_enabled) === 1);
}

function ChatProfessionalLauncher() {
	const location = useLocation();
	const [chatDialogOpen, setChatDialogOpen] = useState(false);
	const [selectedAddress, setSelectedAddress] = useState<ChatAddress | null>(null);
	const [pickerAddresses, setPickerAddresses] = useState<ChatAddress[]>([]);
	const [isAddressResolving, setIsAddressResolving] = useState(false);
	const [isOpeningChat, setIsOpeningChat] = useState(false);

	const [fetchAddresses, { isFetching }] = useLazyGetAddressesListQuery();

	const user = getUser();
	const isApplicant = user?.role === 'Applicant';
	const isExcludedPath = EXCLUDED_PATH_PREFIXES.some((path) =>
		location.pathname.startsWith(path)
	);

	const handleOpenChat = useCallback(async () => {
		if (isFetching || isOpeningChat) {
			return;
		}
		setIsOpeningChat(true);
		setIsAddressResolving(true);
		setSelectedAddress(null);
		setChatDialogOpen(true);

		try {
			// Fresh list so RTK cache from other pages cannot pick the wrong branch
			const result = await fetchAddresses({}, false).unwrap();
			const rawAddresses = (result?.data ?? []) as ChatAddress[];
			const forPicker = filterAddressesForChatPicker(rawAddresses);

			setPickerAddresses(forPicker);

			if (forPicker.length === 1) {
				setSelectedAddress(forPicker[0]);
			} else {
				setSelectedAddress(null);
			}
		} catch {
			setPickerAddresses([]);
			setSelectedAddress(null);
		} finally {
			setIsAddressResolving(false);
			setIsOpeningChat(false);
		}
	}, [isFetching, isOpeningChat, fetchAddresses]);

	const handleSelectAddress = (address: ChatAddress) => {
		setSelectedAddress(address);
	};

	const handleCloseChatDialog = () => {
		setChatDialogOpen(false);
		setIsAddressResolving(false);
	};

	if (!isApplicant || isExcludedPath) {
		return null;
	}

	return (
		<>
			<Tooltip title="Chat with our AI Assistant" placement="top" arrow>
				<Box
					component="span"
					className="chat-professional-launcher"
					sx={{
						position: 'fixed',
						right: 24,
						bottom: 24,
						zIndex: (theme) => theme.zIndex.modal - 1,
						display: 'inline-flex',
						'@keyframes chatProLauncherBob': {
							'0%, 100%': { transform: 'translateY(0)' },
							'50%': { transform: 'translateY(-5px)' }
						},
						animation: 'chatProLauncherBob 2.75s ease-in-out infinite'
					}}
				>
					<IconButton
						aria-label="Open AI assistant chat"
						onClick={handleOpenChat}
						sx={{
							width: 50,
							height: 50,
							maxHeight: 50,
							maxWidth: 50,
							borderRadius: '50%',
							backgroundColor: '#2b7fff',
							color: '#fff',
							flexShrink: 0,
							border: '1px solid rgba(255, 255, 255, 0.75)',
							boxShadow: CHAT_PRO_LAUNCHER_SHADOW_IDLE,
							transition:
								'background-color 0.18s ease, transform 0.18s ease, border-color 0.18s ease, box-shadow 0.22s ease',
							'&:hover': {
								backgroundColor: '#1d4ed8',
								transform: 'translateY(-2px)',
								boxShadow: CHAT_PRO_LAUNCHER_SHADOW_HOVER
							}
						}}
					>
						<ChatBubbleOutlineIcon sx={{ fontSize: 26 }} />
					</IconButton>
				</Box>
			</Tooltip>

			<ChatBoxDialog
				open={chatDialogOpen}
				selectedAddress={selectedAddress}
				onClose={handleCloseChatDialog}
				isAddressResolving={isAddressResolving}
				addresses={pickerAddresses}
				isAddressesLoading={isFetching}
				onSelectAddress={handleSelectAddress}
				onBackToAddressSelection={() => setSelectedAddress(null)}
			/>
		</>
	);
}

export default ChatProfessionalLauncher;

